import {
  CommandDialog,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@parcel/ui';
import { type QueryClient, useQuery } from '@tanstack/react-query';
import {
  Navigate,
  Outlet,
  createRootRouteWithContext,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { Building2, Newspaper, Search, Settings, Tag, Users } from 'lucide-react';
import * as React from 'react';
import { getWorkspaceScopedPath } from '../components/app-sidebar-nav.js';
import { AppSidebar } from '../components/app-sidebar.js';
import { AppUserMenu } from '../components/app-user-menu.js';
import { RecordDetailHost } from '../components/entity-drawer/record-detail-host.js';
import { RecordNavigatorProvider } from '../components/entity-drawer/record-navigator.js';
import { ErrorBoundary } from '../components/error-boundary.js';
import { WorkspaceAccessGate } from '../components/onboarding/workspace-access-gate.js';
import { ParcelLoader } from '../components/parcel-loader.js';
import { apiAccountToRow, useAccountSearchPreview } from '../lib/accounts.js';
import { AnalyticsProvider } from '../lib/analytics.js';
import { useAuth } from '../lib/auth.js';
import { useContactSearchPreview } from '../lib/contacts.js';
import { losesRedirect, storePendingRedirect } from '../lib/pending-redirect.js';
import { apiProjectToRow, useProjectSearchPreview } from '../lib/projects.js';
import { useRecordDetail, validateRecordSearch } from '../lib/record-detail.js';
import { isSpecWorkerConfigured } from '../lib/spec-config.js';
import { isSupabaseConfigured } from '../lib/supabase.js';
import { useResetScrollOnPathChange } from '../lib/use-reset-scroll-on-path-change.js';
import {
  getLegacyWorkspaceDestination,
  getLegacyWorkspaceRedirectTarget,
  getWorkspace,
  getWorkspaceSlugFromPath,
  listWorkspaces,
} from '../lib/workspaces.js';

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  validateSearch: validateRecordSearch,
  component: () => (
    <ErrorBoundary>
      <AnalyticsProvider>
        <RootShell />
      </AnalyticsProvider>
    </ErrorBoundary>
  ),
});

type RootShellMode = 'standalone' | 'auth-required-standalone' | 'app';

export function getRootShellMode(pathname: string, hasSession: boolean): RootShellMode {
  if (pathname === '/') return 'standalone';
  if (pathname === '/signup' || pathname === '/signin') return 'standalone';
  if (/^\/invites\/[^/]+$/.test(pathname)) return 'standalone';
  if (
    pathname === '/signup/workspace' ||
    pathname === '/workspaces/select' ||
    /^\/[^/]+\/welcome$/.test(pathname)
  ) {
    return hasSession ? 'standalone' : 'auth-required-standalone';
  }

  return 'app';
}

/** Exported for the composition test in `root-spec-suspense.test.tsx`. */
export function RootShell() {
  const { session, loading } = useAuth();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  useResetScrollOnPathChange(pathname);
  const search = typeof window === 'undefined' ? '' : window.location.search;
  const shellMode = getRootShellMode(pathname, Boolean(session));

  if (loading) return <AppLoadingScreen />;

  if (shellMode === 'standalone') return <Outlet />;

  if (!session) {
    const signInTarget: string = `/signin?redirect=${encodeURIComponent(`${pathname}${search}`)}`;
    return <Navigate to={signInTarget} replace />;
  }

  const legacyRedirectTarget = getLegacyWorkspaceRedirectTarget(pathname, search);
  if (legacyRedirectTarget) return <LegacyWorkspaceRedirect pathname={pathname} search={search} />;

  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const outlet = workspaceSlug ? (
    <WorkspaceAccessGate workspaceSlug={workspaceSlug}>
      <Outlet />
    </WorkspaceAccessGate>
  ) : (
    <Outlet />
  );

  const inset = (
    // overflow-hidden would make the inset a scroll container, which silently
    // disables every position:sticky inside it (the top bar, Home's detail
    // panel). Keep x clipped, let y stay visible so sticky tracks the page.
    <SidebarInset className="overflow-visible overflow-x-clip">
      <header className="sticky top-0 z-30 flex h-14 min-w-0 items-center gap-3 border-b bg-background/95 px-4 backdrop-blur">
        <SidebarTrigger className="md:hidden" />
        <SearchCommand />
        <AppUserMenu />
      </header>
      {outlet}
    </SidebarInset>
  );

  return (
    <SpecWorkspaceRuntime workspaceSlug={workspaceSlug}>
      <RecordNavigatorProvider>
        <SidebarProvider
          style={
            {
              '--sidebar-width': '14rem',
              '--sidebar-width-icon': '3.25rem',
            } as React.CSSProperties
          }
        >
          <AppSidebar />
          {/* Spec's follow panel is a column between primary navigation and the
              page, so the layout that owns that column wraps the inset here and
              nowhere else. Same build-time gate as the runtime: with no Worker
              URL the inset renders exactly as it always has, and the Spec chunk
              is never fetched. */}
          {isSpecWorkerConfigured() ? <LazySpecFollowLayout>{inset}</LazySpecFollowLayout> : inset}
          {/* Fixed-position, so it is a sibling of the layout rather than a
              child of it: it does not belong to the grid `LazySpecFollowLayout`
              owns, only to the same build-time gate and Suspense boundary. */}
          {isSpecWorkerConfigured() ? <LazySpecLauncher /> : null}
          <RecordDetailHost />
        </SidebarProvider>
      </RecordNavigatorProvider>
    </SpecWorkspaceRuntime>
  );
}

/**
 * Loaded on demand, never statically.
 *
 * The runtime pulls in `@assistant-ui/react`, measured at +121 kB raw /
 * +33 kB gzip on an entry chunk already past vite's 500 kB warning. In
 * production that code can never execute: `VITE_SPEC_URL` is unset, so the
 * gates below short-circuit and this chunk is never fetched. The dynamic import
 * is what keeps it out of the entry graph; the mount points stay exactly where
 * PR 10 needs them.
 *
 * One chunk for all of it, and no nesting: the three lazy elements below
 * resolve from this single import, and the providers are already composed
 * inside it rather than layered here (see `spec/providers.tsx`). Each extra
 * dynamic import would be another round trip the shell waits through, because
 * none of them starts until the one above it has landed.
 */
const importSpecChunk = () => import('../components/spec/providers.js');

const LazySpecProviders = React.lazy(() =>
  importSpecChunk().then((module) => ({ default: module.SpecProviders })),
);

/**
 * The follow panel's layout, out of the SAME module and therefore the same
 * chunk. It cannot come through `SpecProviders` -- it wraps `SidebarInset`
 * alone, not the whole shell -- and a dynamic import of its own module would
 * make the shell wait on a second request after the first one landed.
 */
const LazySpecFollowLayout = React.lazy(() =>
  importSpecChunk().then((module) => ({ default: module.SpecFollowLayout })),
);

/**
 * The bottom-right launcher, out of the SAME module for the same reason:
 * `SpecFollowLayout` wraps `SidebarInset`, but the launcher is a fixed
 * sibling of the whole shell, so it cannot come through either
 * `SpecProviders` or `SpecFollowLayout` without being nested somewhere it
 * does not belong.
 */
const LazySpecLauncher = React.lazy(() =>
  importSpecChunk().then((module) => ({ default: module.SpecLauncher })),
);

/** The app's one "still coming up" screen, shared by auth and the Spec chunk. */
export function AppLoadingScreen() {
  return (
    <div className="grid min-h-svh place-items-center bg-background">
      <ParcelLoader />
    </div>
  );
}

/**
 * The Spec runtime, mounted above the route outlet rather than inside it.
 *
 * Deliberate: PR 10 mounts the same runtime in a left panel that lives outside
 * the route, and a member who navigates mid-answer must keep the thread and the
 * live stream. A route-local provider would drop both on every navigation.
 */
export function SpecWorkspaceRuntime({
  workspaceSlug,
  children,
}: {
  workspaceSlug: string | null;
  children: React.ReactNode;
}) {
  // The same ['workspace', slug] key WorkspaceAccessGate already fetches, so
  // this shares that request rather than adding one.
  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: Boolean(workspaceSlug) && isSupabaseConfigured,
    queryFn: () => getWorkspace(workspaceSlug ?? ''),
  });

  // A build-time constant, so React never switches between these two branches
  // at runtime and the shell is never remounted. In production it is always the
  // first one, and nothing about Spec is downloaded, parsed, or mounted.
  if (!isSpecWorkerConfigured()) return <>{children}</>;

  // NOT `fallback={null}`. The whole app shell is inside this boundary, so a
  // null fallback painted a blank page for as long as the chunk took to arrive
  // rather than telling the member anything. This is the same screen auth shows
  // while it resolves, so the two look like one startup rather than two.
  //
  // The shell cannot simply be lifted out of the boundary: the runtime has to
  // be an ancestor of both consumers (the /spec route and the sidebar's Chats
  // section), and the shell is their only common ancestor. Rendering `children`
  // as the fallback would show the shell, then unmount and remount all of it
  // once the chunk landed, which trades a wait for a flicker plus a double
  // mount of every page. Removing the wait entirely means hoisting the runtime
  // into a module store, the way `spec-launch.ts` already does for launch state.
  return (
    <React.Suspense fallback={<AppLoadingScreen />}>
      <LazySpecProviders
        workspaceId={workspaceQuery.data?.id}
        workspaceLabel={workspaceQuery.data?.name}
      >
        {children}
      </LazySpecProviders>
    </React.Suspense>
  );
}

function LegacyWorkspaceRedirect({ pathname, search }: { pathname: string; search: string }) {
  const [target, setTarget] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (target?.includes('?')) window.location.replace(target);
  }, [target]);

  React.useEffect(() => {
    let active = true;

    async function resolveWorkspaceDestination() {
      setTarget(null);
      setError(null);

      try {
        const destination = getLegacyWorkspaceDestination(await listWorkspaces(), pathname, search);
        // Onboarding cannot carry the redirect in its URL; the tab keeps it for the Enter app button.
        if (losesRedirect(destination ?? '/signup/workspace'))
          storePendingRedirect(`${pathname}${search}`);
        if (active) setTarget(destination ?? '/signup/workspace');
      } catch (err) {
        if (active) {
          setError(err instanceof Error ? err.message : 'Unable to load workspaces.');
        }
      }
    }

    void resolveWorkspaceDestination();

    return () => {
      active = false;
    };
  }, [pathname, search]);

  if (target?.includes('?')) {
    return (
      <div className="grid min-h-svh place-items-center bg-background p-6">
        <ParcelLoader label="Opening workspace" />
      </div>
    );
  }

  if (target) return <Navigate to={target} />;

  return (
    <div className="grid min-h-svh place-items-center bg-background p-6 text-center text-sm text-muted-foreground">
      {error ?? <ParcelLoader label="Opening workspace" />}
    </div>
  );
}

// Debounce the record search so typing doesn't fire an HTTP request per keystroke.
const SEARCH_DEBOUNCE_MS = 250;

// Shortcuts are display-only — there's no G-chord handler (yet).
const SEARCH_NAV_ITEMS = [
  { to: '/feed', label: 'News feed', icon: Newspaper, shortcut: 'G F' },
  { to: '/projects', label: 'Projects', icon: Building2, shortcut: 'G P' },
  { to: '/accounts', label: 'Accounts', icon: Tag, shortcut: 'G A' },
  { to: '/contacts', label: 'Contacts', icon: Users, shortcut: 'G C' },
  { to: '/settings/profile', label: 'Personal settings', icon: Settings, shortcut: 'G S' },
  { to: '/settings/general', label: 'Workspace settings', icon: Settings, shortcut: 'G W' },
  { to: '/settings/members', label: 'Members', icon: Users, shortcut: 'G M' },
] as const;

function SearchCommand() {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [debouncedQuery, setDebouncedQuery] = React.useState('');
  const { open: openRecord } = useRecordDetail();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  React.useEffect(() => {
    const down = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Same ['workspace', slug] cache key the pages use — gated on `open` so the
  // always-mounted header doesn't fetch until the dialog is actually used.
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: Boolean(workspaceSlug) && isSupabaseConfigured && open,
    queryFn: () => getWorkspace(workspaceSlug ?? ''),
  });
  const workspaceId = workspaceQuery.data?.id;

  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query]);

  // Reset the search text whenever the dialog closes.
  React.useEffect(() => {
    if (!open) {
      setQuery('');
      setDebouncedQuery('');
    }
  }, [open]);

  const searchQ = open ? debouncedQuery : '';
  const projectsPreview = useProjectSearchPreview(workspaceId, searchQ);
  const accountsPreview = useAccountSearchPreview(workspaceId, searchQ);
  const contactsPreview = useContactSearchPreview(workspaceId, searchQ);
  const projectResults = projectsPreview.data?.projects ?? [];
  const accountResults = accountsPreview.data?.accounts ?? [];
  const contactResults = contactsPreview.data?.contacts ?? [];

  // shouldFilter={false} (server does the matching), so nav items are
  // filtered manually against the typed text.
  const trimmedQuery = query.trim();
  const visibleNavItems = trimmedQuery
    ? SEARCH_NAV_ITEMS.filter((item) =>
        item.label.toLowerCase().includes(trimmedQuery.toLowerCase()),
      )
    : SEARCH_NAV_ITEMS;

  const searchActive = trimmedQuery.length >= 2;
  const hasRecords =
    projectResults.length > 0 || accountResults.length > 0 || contactResults.length > 0;
  // Disabled queries also report isPending, which covers the debounce-lag
  // window (debouncedQuery hasn't caught up yet) and the workspace fetch.
  const isSearching =
    isSupabaseConfigured &&
    searchActive &&
    projectsPreview.isPending &&
    accountsPreview.isPending &&
    contactsPreview.isPending;
  const showEmpty = searchActive && !isSearching && !hasRecords && visibleNavItems.length === 0;

  const goTo = (to: (typeof SEARCH_NAV_ITEMS)[number]['to']) => {
    setOpen(false);
    navigate({ to: getWorkspaceScopedPath(pathname, to) });
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md border bg-muted/40 px-3 text-left text-sm text-muted-foreground transition-colors hover:bg-muted"
      >
        <span className="flex h-5 w-5 items-center justify-center rounded border bg-background text-[10px] font-medium text-muted-foreground">
          ⌘
        </span>
        <span className="flex h-5 w-5 items-center justify-center rounded border bg-background text-[10px] font-medium text-muted-foreground">
          K
        </span>
        <span className="hidden truncate sm:inline">Search for anything</span>
        <span className="sm:hidden">Search</span>
        <Search className="ml-auto h-4 w-4" />
      </button>
      <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
        <CommandInput
          placeholder="Search projects, accounts, contacts..."
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {isSearching ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">Searching…</div>
          ) : null}
          {/* Manual empty state: cmdk's CommandEmpty keys off its internal
              match count, which shouldFilter={false} + conditional groups
              make unreliable — render a plain row we fully control. */}
          {showEmpty ? <div className="py-6 text-center text-sm">No results found.</div> : null}
          {projectResults.length > 0 ? (
            <CommandGroup heading="Projects">
              {projectResults.map((project) => (
                <CommandItem
                  key={project.id}
                  value={`project:${project.id}`}
                  onSelect={() => {
                    setOpen(false);
                    openRecord(
                      { type: 'project', id: project.id },
                      { seed: apiProjectToRow(project), source: 'command_k' },
                    );
                  }}
                >
                  <Building2 />
                  <span className="truncate">{project.name ?? project.primary_address}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    · {project.city}, {project.state}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          ) : null}
          {accountResults.length > 0 ? (
            <CommandGroup heading="Accounts">
              {accountResults.map((account) => (
                <CommandItem
                  key={account.id}
                  value={`account:${account.id}`}
                  onSelect={() => {
                    setOpen(false);
                    openRecord(
                      { type: 'account', id: account.id },
                      { seed: apiAccountToRow(account), source: 'command_k' },
                    );
                  }}
                >
                  <Tag />
                  <span className="truncate">{account.name}</span>
                  {account.city ? (
                    <span className="truncate text-xs text-muted-foreground">· {account.city}</span>
                  ) : null}
                </CommandItem>
              ))}
            </CommandGroup>
          ) : null}
          {contactResults.length > 0 ? (
            <CommandGroup heading="Contacts">
              {contactResults.map((contact) => (
                <CommandItem
                  key={contact.id}
                  value={`contact:${contact.id}`}
                  onSelect={() => {
                    setOpen(false);
                    openRecord({ type: 'contact', id: contact.id }, { source: 'command_k' });
                  }}
                >
                  <Users />
                  <span className="truncate">{contact.name}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    · {contact.account_name}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          ) : null}
          {visibleNavItems.length > 0 ? (
            <CommandGroup heading="Navigate">
              {visibleNavItems.map((item) => (
                <CommandItem key={item.to} value={`nav:${item.to}`} onSelect={() => goTo(item.to)}>
                  <item.icon />
                  {item.label}
                  <CommandShortcut>{item.shortcut}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          ) : null}
        </CommandList>
      </CommandDialog>
    </>
  );
}
